import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import UserActionError from '@/interfaces/errors/userAction';
import { auth, db, googleOauthScopes } from '@/config/firebase';
import { nowUnix } from '@/utils/date';
import { createWrappedError } from '@/utils/errors';
import * as googleAuth from '@/services/auth/google';
import googleOauthConfig from '@/config/googleOAuth';
import { jsx as _jsx } from "react/jsx-runtime";
const NEW_LINE_CHAR = ' ';
const PREFIX = 'common: hooks: useAuth:';
let GOOGLE_CLIENT = undefined;
export const ERROR_CODES = {
  userNotSignedUp: `${PREFIX}UserNotSignedUp`
};
const authContext = /*#__PURE__*/createContext({
  user: {}
});
const {
  Provider
} = authContext; // AuthProvider is a Context Provider that wraps our app and makes an auth object
// available to any child component that calls the useAuth() hook.

export function AuthProvider(props) {
  const auth = useAuthProvider(); // @ts-ignore

  return /*#__PURE__*/_jsx(Provider, {
    value: auth,
    children: props.children
  });
} // useAuth is a hook that enables any component to subscribe to auth state

export const useAuth = () => {
  return useContext(authContext);
}; // Provider hook that creates auth object and handles state

const useAuthProvider = () => {
  // NOTE: "user" is a messy combo of:
  // Local user record mixed w/ firebase.User at the top-level
  // & a nested `additionalUserInfo` firebase.auth.AdditionalUserInfo
  const {
    0: user,
    1: setUser
  } = useState(null);
  const {
    0: authUser,
    1: setAuthUser
  } = useState();
  const {
    0: credentials,
    1: setCredentials
  } = useState(null);
  const {
    0: isLoading,
    1: setIsLoading
  } = useState(true);
  const isAccessTokenExpired = useMemo(() => nowUnix() - ((user === null || user === void 0 ? void 0 : user.updatedAt) || 0) > ((user === null || user === void 0 ? void 0 : user.expirationTime) || 0), [user === null || user === void 0 ? void 0 : user.expirationTime, user === null || user === void 0 ? void 0 : user.updatedAt]);
  const updateUser = useCallback(async _ref => {
    let {
      id,
      data
    } = _ref;

    try {
      await db.collection('users').doc(id).update(data);
      setUser(_objectSpread(_objectSpread({}, user), data));
    } catch (error) {
      return {
        error
      };
    }
  }, [user]);

  const deleteUser = async () => {
    if (auth.currentUser) {
      try {
        await auth.currentUser.delete();
      } catch (error) {
        return {
          error
        };
      }
    }

    setTimeout(() => setUser(null), 1000);
  };

  const signIn = async _ref2 => {
    let {
      email,
      password
    } = _ref2;

    try {
      const {
        user,
        additionalUserInfo
      } = await auth.signInWithEmailAndPassword(email, password);

      const currentUser = _objectSpread(_objectSpread({}, user), {}, {
        additionalUserInfo
      });

      user && setAuthUser(user);
      setUser(currentUser);
      addUserFirebaseDocData(currentUser);
      return currentUser;
    } catch (error) {
      return {
        error
      };
    }
  }; // Augement an existing user session with google
  // auth token, updating local state including token


  const signInWithGoogle = async credentialToken => {
    let result;

    if (credentialToken) {
      try {
        result = await googleAuth.signInWithCredential(credentialToken);
      } catch (err) {
        throw createWrappedError(`${PREFIX} signInWithGoogle: sign in with credential failed:`, err);
      }
    } else {
      try {
        result = await googleAuth.signInWithPopup();
      } catch (err) {
        throw createWrappedError(`${PREFIX} signInWithGoogle: sign in popup failed:`, err);
      }
    }

    const additionalUserInfo = result.additionalUserInfo;
    const firebaseUser = result.user;

    const currentUser = _objectSpread(_objectSpread({}, firebaseUser), {}, {
      additionalUserInfo
    });

    const oAuthCredential = result.credential;

    if (!firebaseUser) {
      throw Error(`${PREFIX} signInWithGoogle: did not receive expected results from sign-in popup`);
    } // Sanity check, should already exist


    let exists = false;

    try {
      exists = await checkExists(firebaseUser);
    } catch (err) {
      throw createWrappedError(`${PREFIX} signInWithGoogle: failed to check user existence:`, err);
    }

    if (!exists) {
      try {
        await signOut(); // eslint-disable-next-line
      } catch (err) {}

      throw createUserNotSignedUpError();
    }

    setUser(currentUser);
    setAuthUser(firebaseUser);
    setCredentials(oAuthCredential);

    try {
      await addUserFirebaseDocData(currentUser);
    } catch (err) {
      throw createWrappedError(`${PREFIX} signInWithGoogle: failed to add user doc record to user session:`, err);
    }

    return currentUser;
  };

  const signOut = async () => {
    try {
      await auth.signOut();
      return setUser(null);
    } catch (error) {
      return {
        error
      };
    }
  };

  const checkExists = targetUser => {
    if (!targetUser) return Promise.resolve(false);
    return db.collection('users').doc(targetUser.uid).get().then(function (doc) {
      if (doc.exists) {
        return true;
      }

      return false;
    });
  };

  const sendPasswordResetEmail = async email => {
    const response = await auth.sendPasswordResetEmail(email);
    return response;
  }; // Get the user data from Firestore


  const addUserFirebaseDocData = async targetUser => {
    db.collection('users').doc(targetUser.uid).get().then(function (doc) {
      if (doc.exists) {
        setUser(_objectSpread(_objectSpread({}, targetUser), doc.data()));
      }
    });
  }; /// We need to get the user data from the Firestore db


  const handleAuthStateChanged = async targetUser => {
    const exists = await checkExists(targetUser);

    if (targetUser !== null && targetUser !== void 0 && targetUser.uid && exists) {
      setUser(targetUser);
      setAuthUser(targetUser);
      addUserFirebaseDocData(targetUser);
    } else {
      await signOut();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Subscribe to user on mount
    const unsubscribe = auth.onAuthStateChanged(handleAuthStateChanged); // Unsubscribe on cleanup

    return () => unsubscribe(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (user !== null && user !== void 0 && user.uid) {
      // Subscribe to user document
      db.collection('users').doc(user.uid).onSnapshot(doc => {
        setUser(doc.data());
        setIsLoading(false);
      });
    }
  }, [user === null || user === void 0 ? void 0 : user.uid]);
  const getGoogleAccessToken = useCallback(async userId => {
    if (!userId) {
      throw Error(`${PREFIX} getGoogleAccessToken: called without valid user session`);
    }

    let client;

    try {
      client = await createGoogleClient();
    } catch (error) {
      throw Error(`${PREFIX} getGoogleAccessToken: failed to load google auth client: ${error}`);
    }

    return new Promise((resolve, reject) => {
      // @ts-ignore
      client.callback = async response => {
        try {
          const data = await googleAuth.googleCloudToken({
            userUid: userId,
            code: response.code
          });
          resolve(data);
        } catch (error) {
          reject(error);
        }
      }; // @ts-ignore


      client.error_callback = error => {
        reject(error);
      };

      client.requestCode();
    });
  }, []);
  return {
    user,
    authUser,
    credentials,
    getGoogleAccessToken,
    signIn,
    signInWithGoogle,
    signOut,
    sendPasswordResetEmail,
    updateUser,
    deleteUser,
    isAccessTokenExpired,
    isLoading
  };
};

const createGoogleClient = async () => {
  if (GOOGLE_CLIENT) return GOOGLE_CLIENT;

  if (!isGoogleOAuth2ClientLoaded()) {
    try {
      await appendGoogleClientScript();
    } catch (err) {
      throw Error(`${PREFIX} loadGoogleClient: failed to load google auth client: ${err}`);
    }
  }

  const client = window.google.accounts.oauth2.initCodeClient({
    client_id: googleOauthConfig.clientId,
    scope: googleOauthScopes.join(NEW_LINE_CHAR)
  });
  GOOGLE_CLIENT = client;
  return GOOGLE_CLIENT;
}; // Check if all Google Accounts client loaded


function isGoogleOAuth2ClientLoaded() {
  var _window$google, _window$google$accoun, _window$google$accoun2;

  return typeof window.google !== 'undefined' && typeof ((_window$google = window.google) === null || _window$google === void 0 ? void 0 : (_window$google$accoun = _window$google.accounts) === null || _window$google$accoun === void 0 ? void 0 : (_window$google$accoun2 = _window$google$accoun.oauth2) === null || _window$google$accoun2 === void 0 ? void 0 : _window$google$accoun2.initCodeClient) === 'function';
} // Add a script to the DOM to load
// the google client oauth library


function appendGoogleClientScript() {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.setAttribute('src', 'https://accounts.google.com/gsi/client');
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', err => reject(err));
    document.head.appendChild(script);
  });
} // Generate a unique error for users
// that have not signed up for an account


function createUserNotSignedUpError() {
  const err = new UserActionError(`${PREFIX} No account created`); // @ts-ignore

  err.code = ERROR_CODES.userNotSignedUp; // @ts-ignore

  err.userMessage = 'Please sign up for an account before logging in.';
  return err;
}